import pictogramAsset from "assets/img/pictogram/asset.svg"

import { FC } from "react"
import { useTranslation } from "react-i18next"

import { Flex, FlexItem } from "components/Flex"
import Lmid from "components/Lmid/Lmid"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import LmidModalResultItem from "../LmidModalResultItem"
import ExistingLmidData from "models/ExistingLmidData"
import LmidAsset from "models/LmidAsset"
import useCompanies from "hooks/useCompanies"
import Spinner from "components/Spinner/Spinner"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"

interface ILmidModalDifferentTenantOrDisposedProps {
    existingLmidData: ExistingLmidData
    shouldBeEqualToArticleNumber?: string
    /** Replaces the default article-mismatch message. */
    articleMismatchMessage?: string
    onAssetFromDifferentTenantOrDisposedSelect(data: { existingLmidData: ExistingLmidData, assetData: LmidAsset }): void
}

const LmidModalDifferentTenantOrDisposed: FC<ILmidModalDifferentTenantOrDisposedProps> = ({
    existingLmidData,
    shouldBeEqualToArticleNumber,
    articleMismatchMessage,
    onAssetFromDifferentTenantOrDisposedSelect: onAssetFromDifferentTenantOrDisposedSelect,
}) => {
    const { t } = useTranslation()
    const { companies, getCompanyById } = useCompanies()
    const articleError = shouldBeEqualToArticleNumber && existingLmidData.articleNo && existingLmidData.articleNo !== shouldBeEqualToArticleNumber
        ? articleMismatchMessage ?? t('lmidModal.lmidAssignedToDifferentArticle')
        : undefined

    function handleAssetSelect(asset: LmidAsset) {
        onAssetFromDifferentTenantOrDisposedSelect({ existingLmidData, assetData: asset })
    }

    function renderTenantAsset({ asset, isClickable, error }: { asset: LmidAsset, isClickable?: boolean, error?: string }) {
        const company = getCompanyById(asset.tenantId)
        return (
            <LmidModalResultItem
                align="center"
                condensed
                pictogram={{
                    src: pictogramAsset
                }}
                onClick={isClickable ? () => handleAssetSelect(asset) : undefined}>
                {!!company?.name &&
                    <Text className="font-bold mb-1">{company.name}</Text>
                }
                <Text>
                    <Flex>
                        <FlexItem shrink={0}>
                            {t('common.lmid')}:&nbsp;
                        </FlexItem>
                        <Lmid lmid={existingLmidData.lmid} labeled={existingLmidData.isLabeled} />
                    </Flex>
                </Text>
                {!!existingLmidData.articleDescription &&
                    <Text>
                        <Flex>
                            <FlexItem shrink={0}>
                                {t('common.description')}:&nbsp;
                            </FlexItem>
                            <ArticleDescription articleDescription={existingLmidData.articleDescription} />
                        </Flex>
                    </Text>
                }
                {!!error &&
                    <ValidationMessage>{error}</ValidationMessage>
                }
            </LmidModalResultItem>
        )
    }

    return (
        <div className="py-4 max-w-80 mx-auto">
            <Text className="mb-1 text-center mb-3">
                {t(existingLmidData.isFoundInMultipleTenants
                    ? "lmidModal.lmidExistsAnotherTenants"
                    : "lmidModal.lmidExistsAnotherTenant"
                )}
            </Text>
            {existingLmidData.isFoundInMultipleTenants &&
                <div className="mb-3">
                    {t("lmidModal.selectTenantThatShouldBeUsed")}
                </div>
            }
            {companies ?
                <>
                    {existingLmidData.isFoundInMultipleTenants
                        && existingLmidData.assets?.map((asset, idx) => (
                            <div key={idx} className={idx > 0 ? 'mt-3' : ''}>
                                {renderTenantAsset({ asset, isClickable: !articleError, error: articleError })}
                            </div>
                        ))
                    }
                    {!existingLmidData.isFoundInMultipleTenants &&
                        <>
                            {renderTenantAsset({ asset: existingLmidData.assets![0], error: articleError })}
                            {!articleError &&
                                <div className="mt-3 text-center">
                                    <Button
                                        theme="secondary"
                                        label={t("lmidModal.selectTenant")}
                                        onClick={() => handleAssetSelect(existingLmidData.assets![0])}
                                        data-testid="edit-asset-btn"
                                    />
                                </div>
                            }
                        </>
                    }
                </>
                :
                <Spinner />
            }
        </div>
    )
}

export default LmidModalDifferentTenantOrDisposed
